import Carousel from 'common/components/Carousel'
import Image from 'common/components/Image'
import Skeleton from 'common/components/Skeleton'
import theme from 'common/theme'
import GA4 from 'lib/google-analytics-4'
import objectToCamelCase from 'lib/transform/object-to-camel-case'
import { useMemo } from 'react'
import { useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const mainBannerWidth = '808px'
const sideBannerWidth = '360px'

const Container = styled.section`
  width: ${theme.layout.containerWidth};
  height: 350px;
  margin: 20px auto 0;
  display: flex;
  justify-content: space-between;
  overflow: hidden;
`

const MainBanner = styled.section`
  width: ${mainBannerWidth};
`
const SideBanner = styled.section`
  width: ${sideBannerWidth};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

function Banner(): JSX.Element {
  const { data, isLoading } = useStoreState(
    (state) => state.businessSolution,
  )
  const ga4 = GA4()
  const popularSection = useMemo(() => {
    return data?.sections?.find(
      (section) => section.code === 'popular-product',
    )
  }, [data?.sections])
  const popularData = useMemo(() => {
    if (!popularSection?.data) return null
    return objectToCamelCase(popularSection.data)
  }, [popularSection])

  const mainBanners = useMemo(() => {
    return (popularData?.banners ?? [])
      .map((banner, index) => {
        const imagePath = banner?.bannerUrl
        if (!imagePath) return null
        return {
          id: index,
          image: banner.bannerUrl,
          banner,
        }
      })
      .filter(Boolean)
  }, [popularData])

  const sideBanners = useMemo(() => {
    const sideItems =
      popularData?.sideBanners ?? popularData?.sideBanner ?? []
    return sideItems
      .map((banner) => {
        const imagePath = banner?.bannerUrl
        if (!imagePath) return null
        return {
          ...banner,
          image: banner.bannerUrl,
        }
      })
      .filter(Boolean)
  }, [popularData])

  const pushDataLayer = (bannerData) => {
    if (!bannerData) return
    const title = bannerData.title || bannerData.name || ''
    const position = bannerData.position || 'home_banner'
    const sort = bannerData.sort ?? 0
    ga4.trackEvent({
      eventName: 'ec_select_promotion',
      eventCategory: 'Ecommerce',
      params: {
        ec_promotion_id: bannerData.id,
        ec_promotion_name: title,
        ec_creative_name: title.replaceAll(' ', '_'),
        ec_creative_slot: `${position.replaceAll(' ', '_')}_${sort}`,
      },
    })
  }

  const onSlideMainBannerClick = (slide) => {
    const banner = slide?.banner
    pushDataLayer(banner)
    const destination = banner?.bannerRedirectUrl
    if (destination) window.location.href = destination
  }

  const handleSideBannerClick = (banner) => {
    pushDataLayer(banner)
    const destination = banner?.bannerRedirectUrl
    if (destination) window.location.href = destination
  }

  const shouldShowMainBanners = mainBanners.length > 0 && !isLoading
  const shouldShowSideBanners = sideBanners.length > 0 && !isLoading

  return (
    <Container>
      <MainBanner>
        {!shouldShowMainBanners && (
          <Skeleton
            data-testid="main-banner-skeleton"
            height="100%"
          />
        )}
        {shouldShowMainBanners && (
          <Carousel
            images={mainBanners}
            imageRadius={theme.radius.base}
            width={mainBannerWidth}
            height="350px"
            margin="0"
            onImageClick={onSlideMainBannerClick}
          />
        )}
      </MainBanner>
      <SideBanner>
        {(!shouldShowSideBanners || isLoading) && (
          <>
            <Skeleton height="170px" />
            <Skeleton height="170px" />
          </>
        )}
        {shouldShowSideBanners &&
          sideBanners.slice(0, 2).map((sideBanner) => {
            return (
              <a
                href={sideBanner.bannerRedirectUrl}
                key={sideBanner.id || sideBanner.title}
                onClick={() => handleSideBannerClick(sideBanner)}
                target="_blank"
                rel="noreferrer"
              >
                <Image
                  src={sideBanner.bannerUrl}
                  width={sideBannerWidth}
                  height="170px"
                  radius={theme.radius.base}
                  objectFit="cover"
                />
              </a>
            )
          })}
      </SideBanner>
    </Container>
  )
}
export default Banner
